import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import supabase from "@/supabase";
import { useSession } from "@/hooks/useSessionHook";
import { getAppUrl } from "@/lib/app-url";

export function AuthPage() {
  const location = useLocation();
  const { session } = useSession();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const appUrl = getAppUrl();

  // Handle forgot password flow
  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }

      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${appUrl}/auth/update-password`,
        });

      if (error) throw error;

      setShowForgotPasswordModal(true);
    } catch (e) {
      const error = e as Error;
      toast.error(error.message || "Failed to send reset email");
    }
  };
  
  // Show message if redirected from expired password link
  useEffect(() => {
    if (location.state?.resetEmail) {
      toast.info("Your password reset link has expired. Please request a new one.");
      setActiveTab("login");
    }
  }, [location.state]);

  // Handle login with email and password
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoading(true);
    try {
      const { error: supabaseError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (supabaseError) throw supabaseError;

      toast.success("Logged in successfully!");
      navigate("/protected");
    } catch (e) {
      const error = e as Error;
      setLoginError(error.message || "Failed to log in");
    } finally {
      setLoading(false);
    }
  };

  // Handle registration with email and password
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);
    setLoading(true);
    
    try {
      // Attempt registration
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${appUrl}/protected`,
        },
      });

      // Check if signUp response indicates existing user
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        throw new Error("User already registered");
      }

      if (error) {
        throw error;
      }

      toast.success(
        "Registration successful! Please check your email for verification."
      );
      setShowSuccessModal(true);
    } catch (e) {
      const error = e as Error;
      let errorMessage = error.message || "Failed to register";
      
      if (error.message.includes("User already registered") || 
          error.message.includes("already exists") || 
          error.message.includes("already in use") ||
          error.message.includes("violates row-level security policy")) {
        errorMessage = "The email address you entered is already taken. If this isyour correct email address, please go back to the Login tab and click Forgot password to set a new password.";
      }
      
      setRegisterError(errorMessage);
      setShowSuccessModal(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      navigate("/protected");
    }
  }, [session, navigate]);

  return (
    <div className="grid min-h-svh lg:grid-cols-2 bg-black">
      <div className="flex flex-col gap-4 p-6 md:p-10 bg-primary/70">
        <Link to="/" className="text-secondary hover:underline">
          Home
        </Link>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-md p-8 pb-18 pt-12 rounded-md shadow-md bg-white">
            <Tabs 
              value={activeTab} 
              onValueChange={setActiveTab}
              className="w-full"
            >
              <div className="flex flex-col items-center gap-2 mb-2 text-center">
                <img src="/logo.png" alt="JoyRide logo" width={150} height={150} />
                <h1 className="text-2xl font-bold text-primary">🚛 joyride</h1>
                <p className="text-primary opacity-80 font-medium">
                  Bringing the world together
                </p>
              </div>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="register">Register</TabsTrigger>
              </TabsList>

              {/* Login Tab Content */}
              <TabsContent value="login">
                <form className="flex flex-col gap-6" onSubmit={handleLogin}>
                  <div className="grid gap-6">
                    <div className="grid gap-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="m@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password">Password</Label>
                        <Button 
                          variant="link" 
                          className="text-primary p-0 h-auto"
                          onClick={handleForgotPassword}
                          disabled={loading}
                        >
                          Forgot password?
                        </Button>
                      </div>
                      <PasswordInput
                        id="login-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Logging In..." : "Login"}
                    </Button>
                    {loginError && (
                      <p className="text-red-500 text-sm mt-2 text-center">
                        {loginError}
                      </p>
                    )}
                  </div>
                </form>
              </TabsContent>

              {/* Register Tab Content */}
              <TabsContent value="register">
                <form className="flex flex-col gap-6" onSubmit={handleRegister}>
                  <div className="grid gap-6">
                    <div className="grid gap-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="m@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="register-password">Password</Label>
                      <PasswordInput
                        id="register-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Creating Account..." : "Register"}
                    </Button>
                    {registerError && (
                      <p className="text-red-500 text-sm mt-2 text-center">
                        {registerError}
                      </p>
                    )}
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      <div className="relative hidden bg-muted lg:block">
        <img
          src="/image.avif"
          alt="Background"
          className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
        />
      </div>

      {/* Registration Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Registration Successful</h2>
            <p className="mb-4">
              We have sent you a confirmation email. Click on the link in the email to access your Joyride account.
            </p>
            <Button 
              className="w-full"
              onClick={() => {
                setShowSuccessModal(false);
                setActiveTab("login");
                setEmail("");
                setPassword("");
              }}
            >
              OK
            </Button>
          </div>
        </div>
      )}

      {showForgotPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Password Reset Email Sent</h2>
            <p className="mb-4">
              An email with a reset password link has been sent to your email address. Click this link if you want to proceed with resetting your password.
            </p>
            <Button 
              className="w-full"
              onClick={() => {
                setShowForgotPasswordModal(false);
              }}
            >
              OK
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
